import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ChevronsLeftRightIcon from './icons/ChevronsLeftRightIcon';
import ColumnsIcon from './icons/ColumnsIcon';
import LayersIcon from './icons/LayersIcon';
import PictureInPictureIcon from './icons/PictureInPictureIcon';
import ZoomInIcon from './icons/ZoomInIcon';
import ZoomOutIcon from './icons/ZoomOutIcon';
import RefreshCwIcon from './icons/RefreshCwIcon';
import ImageInImageViewer from './ImageInImageViewer';

interface InteractiveComparisonViewerProps {
    beforeImageUrl: string;
    afterImageUrl: string;
    initialAspectRatio?: number | null;
    isFullscreen?: boolean;
    onToggleFullscreen?: () => void;
    className?: string;
}

const InteractiveComparisonViewer: React.FC<InteractiveComparisonViewerProps> = ({
    beforeImageUrl,
    afterImageUrl,
    initialAspectRatio,
    isFullscreen = false,
    onToggleFullscreen,
    className = ''
}) => {
    const [comparisonMode, setComparisonMode] = useState<'slider' | 'side-by-side' | 'overlay' | 'image-in-image'>('side-by-side');
    const [sliderPosition, setSliderPosition] = useState(50);
    const [isSliderDragging, setIsSliderDragging] = useState(false);
    const [viewState, setViewState] = useState({ scale: 1, x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
    const [isControlsVisible, setIsControlsVisible] = useState(true);
    const [isOverlayActive, setIsOverlayActive] = useState(false);
    // Default to 1 (Square) if not provided
    const [aspectRatio, setAspectRatio] = useState<number | null>(initialAspectRatio || 1);

    const controlsTimeoutRef = useRef<number | null>(null);
    const viewerRef = useRef<HTMLDivElement>(null);
    const leftImageRef = useRef<HTMLDivElement>(null);
    const rightImageRef = useRef<HTMLDivElement>(null);

    // --- Image Loading & Aspect Ratio ---
    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const { naturalWidth, naturalHeight } = e.currentTarget;
        if (naturalHeight > 0) {
            setAspectRatio(naturalWidth / naturalHeight);
        }
    };

    // --- Controls Visibility ---
    const hideControls = useCallback(() => {
        if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
        setIsControlsVisible(false);
    }, []);

    const showControls = useCallback(() => {
        setIsControlsVisible(true);
        if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
        controlsTimeoutRef.current = window.setTimeout(hideControls, 4000);
    }, [hideControls]);

    useEffect(() => {
        showControls();
        return () => {
            if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
        };
    }, [showControls]);

    // --- Zoom & Pan Logic ---
    const handleZoom = useCallback((delta: number, clientX?: number, clientY?: number) => {
        let targetRect = viewerRef.current?.getBoundingClientRect();

        if (comparisonMode === 'side-by-side' && clientX !== undefined && clientY !== undefined) {
            const leftRect = leftImageRef.current?.getBoundingClientRect();
            const rightRect = rightImageRef.current?.getBoundingClientRect();

            if (leftRect && clientX >= leftRect.left && clientX <= leftRect.right && clientY >= leftRect.top && clientY <= leftRect.bottom) {
                targetRect = leftRect;
            } else if (rightRect && clientX >= rightRect.left && clientX <= rightRect.right && clientY >= rightRect.top && clientY <= rightRect.bottom) {
                targetRect = rightRect;
            } else if (leftRect) {
                targetRect = leftRect;
            }
        }

        if (!targetRect) return;

        const mouseX = clientX ?? targetRect.left + targetRect.width / 2;
        const mouseY = clientY ?? targetRect.top + targetRect.height / 2;

        setViewState(prev => {
            const newScale = Math.max(0.5, Math.min(prev.scale * delta, 8));
            const rectCenterX = targetRect!.left + targetRect!.width / 2;
            const rectCenterY = targetRect!.top + targetRect!.height / 2;
            const worldX = (mouseX - (rectCenterX + prev.x)) / prev.scale;
            const worldY = (mouseY - (rectCenterY + prev.y)) / prev.scale;
            const newX = mouseX - rectCenterX - worldX * newScale;
            const newY = mouseY - rectCenterY - worldY * newScale;

            return { scale: newScale, x: newX, y: newY };
        });
    }, [comparisonMode]);

    const handleWheel = (e: React.WheelEvent) => {
        if (comparisonMode === 'image-in-image') return;
        e.preventDefault();
        handleZoom(e.deltaY > 0 ? 0.9 : 1.1, e.clientX, e.clientY);
    };

    const handleDoubleClick = (e: React.MouseEvent) => {
        if (comparisonMode === 'image-in-image') return;
        if (viewState.scale > 1.5) {
            setViewState({ scale: 1, x: 0, y: 0 });
        } else {
            handleZoom(2.5, e.clientX, e.clientY);
        }
    };

    const onPanStart = (e: React.MouseEvent) => {
        if (comparisonMode === 'image-in-image') return;
        e.preventDefault();
        setIsPanning(true);
        setStartPoint({ x: e.clientX - viewState.x, y: e.clientY - viewState.y });
    };

    const onPanMove = (e: React.MouseEvent) => {
        if (!isPanning) return;
        const newX = e.clientX - startPoint.x;
        const newY = e.clientY - startPoint.y;
        setViewState(prev => ({ ...prev, x: newX, y: newY }));
    };

    const onPanEnd = () => setIsPanning(false);

    // --- Slider Logic ---
    const startSliderDrag = (e: React.MouseEvent | React.TouchEvent) => {
        e.stopPropagation();
        setIsSliderDragging(true);
    };

    const stopSliderDrag = useCallback(() => {
        setIsSliderDragging(false);
    }, []);

    const onSliderDrag = useCallback((clientX: number) => {
        if (!isSliderDragging || !viewerRef.current) return;
        const rect = viewerRef.current.getBoundingClientRect();
        const percentage = ((clientX - rect.left) / rect.width) * 100;
        setSliderPosition(Math.max(0, Math.min(percentage, 100)));
    }, [isSliderDragging]);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => onSliderDrag(e.clientX);
        const handleTouchMove = (e: TouchEvent) => onSliderDrag(e.touches[0].clientX);

        if (isSliderDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('touchmove', handleTouchMove);
            window.addEventListener('mouseup', stopSliderDrag);
            window.addEventListener('touchend', stopSliderDrag);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('mouseup', stopSliderDrag);
            window.removeEventListener('touchend', stopSliderDrag);
        };
    }, [isSliderDragging, onSliderDrag, stopSliderDrag]);

    const resetView = () => setViewState({ scale: 1, x: 0, y: 0 });

    // --- Render Content ---
    const renderContent = () => {
        if (comparisonMode === 'image-in-image') {
            return (
                <div className="w-full h-full flex items-center justify-center p-4">
                    <div className="relative w-full h-full" style={{ aspectRatio: aspectRatio ? `${aspectRatio}` : '1' }}>
                        <ImageInImageViewer
                            beforeImageUrl={beforeImageUrl}
                            afterImageUrl={afterImageUrl}
                            aspectRatio={aspectRatio || 1}
                        />
                    </div>
                </div>
            );
        }

        const contentStyle = {
            transform: `translate(${viewState.x}px, ${viewState.y}px) scale(${viewState.scale})`,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            willChange: 'transform',
        };

        if (comparisonMode === 'side-by-side') {
            return (
                <div className="w-full h-full flex flex-col md:flex-row items-center justify-center px-4 gap-4">
                    <div ref={leftImageRef} className="relative flex-1 w-full md:w-auto h-full overflow-hidden rounded-xl bg-black/20 border border-white/5 shadow-inner">
                        <div className="w-full h-full flex items-center justify-center" style={contentStyle}>
                            <img src={beforeImageUrl} alt="Before" className="max-h-full max-w-full object-contain" draggable="false" />
                        </div>
                        <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-md text-white text-xs font-bold uppercase px-3 py-1.5 rounded-full pointer-events-none z-10 border border-white/10">Before</div>
                    </div>
                    <div ref={rightImageRef} className="relative flex-1 w-full md:w-auto h-full overflow-hidden rounded-xl bg-black/20 border border-white/5 shadow-inner">
                        <div className="w-full h-full flex items-center justify-center" style={contentStyle}>
                            <img src={afterImageUrl} alt="After" className="max-h-full max-w-full object-contain" draggable="false" />
                        </div>
                        <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md text-white text-xs font-bold uppercase px-3 py-1.5 rounded-full pointer-events-none z-10 border border-white/10">After</div>
                    </div>
                </div>
            );
        }

        if (comparisonMode === 'overlay') {
            return (
                <div className="transition-transform duration-100 ease-out origin-center w-full h-full" style={contentStyle}>
                    <div
                        className="relative h-full w-full flex items-center justify-center"
                        onMouseEnter={() => setIsOverlayActive(true)}
                        onMouseLeave={() => setIsOverlayActive(false)}
                        onClick={() => setIsOverlayActive(prev => !prev)}
                    >
                        <div className="relative max-h-full max-w-full" style={{ aspectRatio: aspectRatio ? `${aspectRatio}` : '1' }}>
                            <img src={beforeImageUrl} alt="Before" className="max-h-full max-w-full object-contain" draggable="false" />
                            <motion.div
                                className="absolute inset-0 w-full h-full"
                                animate={{ opacity: isOverlayActive ? 1 : 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <img src={afterImageUrl} alt="After" className="w-full h-full object-contain" draggable="false" />
                            </motion.div>
                            <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-md text-white text-xs font-bold uppercase px-3 py-1.5 rounded-full pointer-events-none transition-opacity duration-300 border border-white/10">
                                {isOverlayActive ? 'AFTER' : 'BEFORE'}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Slider Mode
        return (
            <div className="transition-transform duration-100 ease-out origin-center w-full h-full" style={contentStyle}>
                <div className="relative h-full w-full flex items-center justify-center select-none">
                    <div className="relative max-h-full max-w-full shadow-2xl shadow-black/50 rounded-lg overflow-hidden" style={{ aspectRatio: aspectRatio ? `${aspectRatio}` : '1' }}>
                        <img onLoad={handleImageLoad} src={afterImageUrl} alt="After" className="max-h-full max-w-full object-contain" draggable="false" />
                        <div className="absolute inset-0 w-full h-full overflow-hidden" style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}>
                            <img src={beforeImageUrl} alt="Before" className="absolute inset-0 w-full h-full object-contain" draggable="false" />
                        </div>
                        <div
                            className="absolute top-0 bottom-0 -ml-px w-0.5 bg-teal-400/80 cursor-ew-resize group z-10"
                            style={{ left: `${sliderPosition}%`, boxShadow: '0 0 20px rgba(20,184,166,0.5)' }}
                            onMouseDown={startSliderDrag}
                            onTouchStart={startSliderDrag}
                        >
                            <div className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 bg-black/20 backdrop-blur-md border border-white/30 shadow-lg group-hover:scale-110 ${isSliderDragging ? 'scale-110 bg-teal-500/80 border-white' : ''}`}>
                                <ChevronsLeftRightIcon className="w-5 h-5 text-white drop-shadow-md" />
                            </div>
                        </div>
                        <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-md text-white text-xs font-bold uppercase px-3 py-1.5 rounded-full pointer-events-none border border-white/10">After</div>
                        <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md text-white text-xs font-bold uppercase px-3 py-1.5 rounded-full pointer-events-none border border-white/10">Before</div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div
            className={`relative w-full h-full bg-zinc-950/50 overflow-hidden ${className}`}
            onMouseMove={showControls}
            onMouseLeave={hideControls}
        >
            {/* Main Viewer Area */}
            <div
                ref={viewerRef}
                className="w-full h-full flex items-center justify-center overflow-hidden"
                onWheel={handleWheel}
                onMouseDown={onPanStart}
                onMouseMove={onPanMove}
                onMouseUp={onPanEnd}
                onMouseLeave={onPanEnd}
                onDoubleClick={handleDoubleClick}
                style={{ cursor: comparisonMode === 'image-in-image' ? 'default' : (isPanning ? 'grabbing' : 'grab') }}
            >
                {renderContent()}
            </div>

            {/* Floating Controls Bar */}
            <AnimatePresence>
                {isControlsVisible && (
                    <motion.div
                        initial={{ y: 100, opacity: 0, x: '-50%' }}
                        animate={{ y: 0, opacity: 1, x: '-50%' }}
                        exit={{ y: 100, opacity: 0, x: '-50%' }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute bottom-6 left-1/2 z-[40] flex flex-col items-center gap-3 w-full max-w-[90vw] sm:max-w-fit"
                        style={{ transform: 'translateX(-50%)' }}
                    >
                        <div className="p-1.5 rounded-full bg-zinc-950/70 backdrop-blur-2xl border border-white/10 flex flex-wrap justify-center items-center gap-2 shadow-2xl shadow-black/50 max-w-full overflow-x-auto scrollbar-hide">
                            {/* Mode Switcher */}
                            <div className="p-1 rounded-full bg-white/5 flex items-center gap-1 shrink-0">
                                {[
                                    { id: 'slider', icon: ChevronsLeftRightIcon, label: 'Slider' },
                                    { id: 'side-by-side', icon: ColumnsIcon, label: 'Side' },
                                    { id: 'overlay', icon: LayersIcon, label: 'Overlay' },
                                    { id: 'image-in-image', icon: PictureInPictureIcon, label: 'Magnify' }
                                ].map((mode) => (
                                    <button
                                        key={mode.id}
                                        onClick={() => setComparisonMode(mode.id as any)}
                                        className={`relative px-3 py-2 text-xs font-semibold rounded-full transition-all duration-300 flex items-center gap-2 ${comparisonMode === mode.id ? 'text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
                                        title={mode.label}
                                    >
                                        {comparisonMode === mode.id && (
                                            <motion.div layoutId="viewerModeActive" className="absolute inset-0 bg-white/10 rounded-full border border-white/5" transition={{ type: 'spring', stiffness: 300, damping: 25 }} />
                                        )}
                                        <mode.icon className="w-4 h-4 relative z-10" />
                                        <span className="relative z-10 hidden md:inline">{mode.label}</span>
                                    </button>
                                ))}
                            </div>

                            {/* Zoom Controls */}
                            {comparisonMode !== 'image-in-image' && (
                                <>
                                    <div className="w-px h-6 bg-white/10 mx-1 hidden sm:block" />
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button onClick={() => handleZoom(0.8)} className="p-2 text-zinc-300 hover:text-white rounded-full hover:bg-white/10 transition-colors focus:outline-none" title="Zoom Out">
                                            <ZoomOutIcon className="w-4 h-4" />
                                        </button>
                                        <span className="text-xs font-mono text-zinc-500 w-10 text-center select-none hidden sm:block">
                                            {Math.round(viewState.scale * 100)}%
                                        </span>
                                        <button onClick={() => handleZoom(1.2)} className="p-2 text-zinc-300 hover:text-white rounded-full hover:bg-white/10 transition-colors focus:outline-none" title="Zoom In">
                                            <ZoomInIcon className="w-4 h-4" />
                                        </button>
                                        <button onClick={resetView} className="p-2 text-zinc-300 hover:text-white rounded-full hover:bg-white/10 transition-colors focus:outline-none" title="Reset View">
                                            <RefreshCwIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default InteractiveComparisonViewer;
